/**
 * What a default value is allowed to say — the editor's half of the rule the
 * server enforces in api/Properties/DefaultValueRules.cs.
 *
 * The cases live in tests/fixtures/reference-rules.json and are run by both
 * implementations. Add a case there before changing either.
 */

/** A binary value is bytes, so it has nothing to substitute into text. */
const NOT_REFERENCEABLE = ['Image', 'Table', 'DocumentDictionary'];

export interface OrderedProperty {
  tokenName: string;
  propertyType: string;
}

const REFERENCE_LOOKUP = /\{\{@((?:\$[A-Za-z]+(?:>[A-Za-z]+)*|[^{}>$])+?)((?:>[^{}]*)?)\}\}/g;
export const REFERENCE = /\{\{(?![@$])([^{}>]+?)((?:>[^{}]*)?)\}\}/g;
const VARIABLE =
  /\{\{\$(Today|Year|FirstName|LastName|FullName|Email|Country|TemplateName|TemplateLanguage)((?:>[A-Za-z]+)*)\}\}/gi;
// Broader than VARIABLE: any `{{$Word}}` shape, known or not, so a typo can be flagged.
const VARIABLE_ATTEMPT = /\{\{\$([A-Za-z]+)(?:>[^{}]*)?\}\}/g;
// The key-content equivalent of VARIABLE_ATTEMPT: any `$Word` inside a {{@...}} key.
const KEY_VARIABLE_ATTEMPT = /\$([A-Za-z]+)(?![A-Za-z])/g;

/** The `{{Property}}` names an expression refers to; `{{@Key}}` is a lookup, not a property. */
export function references(value: string): string[] {
  return [...value.matchAll(REFERENCE)].map((match) => match[1].trim());
}

/**
 * The `{{$Name}}` names an expression attempts to use, known or not. Covers bare `{{$Name}}`
 * and any `$Name` embedded in a `{{@...}}` key (excluding `$FieldName`, the driver placeholder
 * rather than a system variable).
 */
export function systemVariableNames(value: string): string[] {
  const bare = [...value.matchAll(VARIABLE_ATTEMPT)].map((match) => match[1]);
  const embedded = [...value.matchAll(REFERENCE_LOOKUP)]
    .flatMap((match) => [...match[1].matchAll(KEY_VARIABLE_ATTEMPT)].map((inner) => inner[1]))
    .filter((name) => name.toLowerCase() !== 'fieldname');
  return [...bare, ...embedded];
}

/** Every `>chain` an expression carries, in any syntax. */
export function transformChains(value: string): string[] {
  return [
    ...[...value.matchAll(VARIABLE), ...value.matchAll(REFERENCE)].map((match) => match[2]),
    ...[...value.matchAll(REFERENCE_LOOKUP)].map((match) => match[2])
  ].filter((chain) => chain.length > 0);
}

function unknownTransforms(chain: string, available: string[]): string[] {
  return chain
    .split('>')
    .filter(Boolean)
    .filter((name) => !available.some((known) => known.toLowerCase() === name.trim().toLowerCase()));
}

export function validateDefaultValue(
  properties: OrderedProperty[],
  index: number,
  defaultValue: string,
  vocabulary: { variables: { name: string }[]; transforms: string[]; expandable: Record<string, string[]> }
): string[] {
  if (!defaultValue) return [];

  const errors: string[] = [];

  for (const chain of transformChains(defaultValue)) {
    for (const name of unknownTransforms(chain, vocabulary.transforms)) {
      errors.push(`'${name}' is not a transform. Available: ${vocabulary.transforms.join(', ')}.`);
    }
  }

  for (const name of systemVariableNames(defaultValue)) {
    if (!vocabulary.variables.some((known) => known.name.toLowerCase() === name.toLowerCase())) {
      const available = vocabulary.variables.map((known) => `$${known.name}`).join(', ');
      errors.push(`'$${name}' is not a system variable. Available: ${available}.`);
    }
  }

  for (const token of references(defaultValue)) {
    const dot = token.indexOf('.');
    const name = dot < 0 ? token : token.slice(0, dot);
    const field = dot < 0 ? null : token.slice(dot + 1);

    const position = properties.findIndex(
      (property) => property.tokenName.toLowerCase() === name.toLowerCase()
    );

    if (position < 0) {
      errors.push(`'${name}' is not a property of this template.`);
      continue;
    }

    if (position >= index) {
      errors.push(`'${name}' is filled in after this property, so it would always be empty.`);
      continue;
    }

    const type = properties[position].propertyType;

    if (field === null) {
      if (NOT_REFERENCEABLE.includes(type)) {
        errors.push(`'${name}' holds a file, so it cannot be used as text.`);
      } else if (vocabulary.expandable[type]?.length) {
        // The whole record is JSON; only a field of it is text.
        const fields = vocabulary.expandable[type];
        const example = fields.find((field) => field.toLowerCase() === 'name') ?? fields[0];
        errors.push(`'${name}' is a ${type}, so choose a field such as ${name}.${example}.`);
      }
      continue;
    }

    const fields = vocabulary.expandable[type];
    if (!fields) {
      errors.push(`'${name}' is a ${type}, so it has no sub-fields.`);
    } else if (!fields.some((known) => known.toLowerCase() === field.toLowerCase())) {
      errors.push(`'${name}' has no '${field}' field. Available: ${fields.join(', ')}.`);
    }
  }

  return errors;
}
